import { FileUp, FileText } from "lucide-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";

interface UploadDropzoneProps {
  readonly disabled?: boolean;
  readonly onSelect: (file: File) => void;
}

export function UploadDropzone({
  disabled = false,
  onSelect,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const choose = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFile = (file: File | undefined) => {
    if (file !== undefined && !disabled) onSelect(file);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files[0]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose();
    }
  };

  return (
    <div
      className={`upload-dropzone${dragging ? " is-dragging" : ""}`}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDragging(false);
        }
      }}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label="PDF 文件拖放区域"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept=".pdf,application/pdf"
        aria-label="选择 PDF 文件"
        onChange={handleChange}
        disabled={disabled}
      />
      <div className="dropzone-file-icon" aria-hidden="true">
        <FileText size={37} strokeWidth={1.7} />
      </div>
      <p className="dropzone-title">拖拽 PDF 文件到这里，或点击选择文件</p>
      <p className="dropzone-copy">
        最大 25 MiB · 文件直传私有 R2 · 关闭页面后自动清理
      </p>
      <button
        className="primary-action"
        type="button"
        onClick={choose}
        disabled={disabled}
      >
        <FileUp size={21} />
        选择 PDF 文件
      </button>
    </div>
  );
}
